import { useState, type ComponentPropsWithRef } from "react";

import { DATE_PLACEHOLDER, displayToIso, isoToDisplay, maskDate } from "@/lib/date-field";

import { TextField, type TextFieldProps } from "./Field";

export interface DateFieldProps extends Omit<
  TextFieldProps,
  "value" | "onChange" | "type" | "defaultValue"
> {
  /** An ISO date, "YYYY-MM-DD", or "" for none. */
  value: string;
  /** Called with an ISO date once the text is a real date, and with "" while it is not. */
  onValueChange: (iso: string) => void;
}

/**
 * Our own date field in place of the browser's: type DD/MM/YYYY, the slashes go in for you, and the value is an ISO
 * date. It shows the app's date order whatever the browser's language.
 */
export function DateField({ value, onValueChange, error, className, ...props }: DateFieldProps) {
  const [typed, setTyped] = useState(() => isoToDisplay(value));
  const [lastValue, setLastValue] = useState(value);
  // A value set from outside (a reset, a loaded profile) replaces what was typed.
  if (value !== lastValue) {
    setLastValue(value);
    if (displayToIso(typed) !== value) setTyped(isoToDisplay(value));
  }
  const incomplete = typed.length === DATE_PLACEHOLDER.length && displayToIso(typed) === undefined;
  const inputProps: ComponentPropsWithRef<"input"> = {
    inputMode: "numeric",
    autoComplete: "off",
    placeholder: DATE_PLACEHOLDER,
    maxLength: DATE_PLACEHOLDER.length,
  };
  return (
    <TextField
      {...props}
      {...inputProps}
      className={className}
      value={typed}
      error={error ?? (incomplete ? "That is not a date." : undefined)}
      onChange={(event) => {
        const next = maskDate(event.target.value);
        setTyped(next);
        const iso = displayToIso(next) ?? "";
        setLastValue(iso);
        onValueChange(iso);
      }}
    />
  );
}
